'use strict';

const { drawPlane, drawRadianceSparkles } = require('../ui/draw');
const { COLORS } = require('../config/constants');
const { getPlaneRadiance } = require('../ui/plane-radiance');

class Player {
  constructor(x, y, appearance) {
    this.x = x;
    this.y = y;
    this.targetX = x;
    this.targetY = y;
    const requestedScale = Number(appearance && appearance.scale);
    this.scale = Number.isFinite(requestedScale) && requestedScale > 0 ? requestedScale : 1;
    this.radius = 18 * this.scale;
    this.visualRadius = 48 * this.scale;
    this.dragRadius = 75 * this.scale;
    this.followSpeed = 18;
    this.planeColor = (appearance && appearance.planeColor) || COLORS.cyan;
    this.trailColor = (appearance && appearance.trailColor) || COLORS.cyan;
    this.level = Math.max(1, Math.min(99, Math.floor(Number(appearance && appearance.level) || 1)));
    this.mode = 'normal';
    this.fighterImage = null;
  }

  setMode(mode, fighterImage) {
    this.mode = mode === 'fighter' ? 'fighter' : 'normal';
    this.fighterImage = fighterImage || this.fighterImage;
    if (this.mode === 'fighter') {
      this.radius = 16 * this.scale;
      this.visualRadius = 54 * this.scale;
      this.dragRadius = 82 * this.scale;
    } else {
      this.radius = 18 * this.scale;
      this.visualRadius = 48 * this.scale;
      this.dragRadius = 75 * this.scale;
    }
  }

  getCannonPositions(alternate) {
    const offset = (alternate ? 22 : -22) * this.scale;
    return { x: this.x + offset, y: this.y - 28 * this.scale };
  }

  setTarget(x, y) {
    this.targetX = x;
    this.targetY = y;
  }

  moveBy(dx, dy, bounds) {
    const margin = this.visualRadius;
    const nextX = Math.max(bounds.left + margin, Math.min(bounds.right - margin, this.x + dx));
    const nextY = Math.max(bounds.top + margin, Math.min(bounds.bottom - margin, this.y + dy));
    this.x = nextX;
    this.y = nextY;
    this.targetX = nextX;
    this.targetY = nextY;
  }

  update(dt, bounds) {
    const factor = Math.min(1, this.followSpeed * dt);
    this.x += (this.targetX - this.x) * factor;
    this.y += (this.targetY - this.y) * factor;
    const margin = this.visualRadius;
    this.x = Math.max(bounds.left + margin, Math.min(bounds.right - margin, this.x));
    this.y = Math.max(bounds.top + margin, Math.min(bounds.bottom - margin, this.y));
    this.targetX = Math.max(bounds.left + margin, Math.min(bounds.right - margin, this.targetX));
    this.targetY = Math.max(bounds.top + margin, Math.min(bounds.bottom - margin, this.targetY));
  }

  contains(x, y) {
    const dx = x - this.x;
    const dy = y - this.y;
    return dx * dx + dy * dy <= this.dragRadius * this.dragRadius;
  }

  draw(ctx) {
    if (this.mode === 'fighter') {
      this.drawFighter(ctx);
      return;
    }
    const scale = this.scale;
    const now = typeof performance !== 'undefined' && performance.now ? performance.now() / 1000 : Date.now() / 1000;
    const radiance = getPlaneRadiance(this.level, this.planeColor, now);
    ctx.save();
    const gradient = ctx.createLinearGradient(
      this.x,
      this.y + 24 * scale,
      this.x,
      this.y + 82 * scale
    );
    gradient.addColorStop(0, radiance.glowColor || this.trailColor);
    gradient.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(this.x - 12 * scale, this.y + 22 * scale);
    ctx.lineTo(this.x, this.y + 82 * scale);
    ctx.lineTo(this.x + 12 * scale, this.y + 22 * scale);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
    drawPlane(ctx, this.x, this.y, scale, this.planeColor, radiance);
    ctx.save();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.72)';
    ctx.beginPath();
    ctx.arc(this.x, this.y + 4 * scale, 4 * scale, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  drawFighter(ctx) {
    const scale = this.scale;
    const now = typeof performance !== 'undefined' && performance.now ? performance.now() / 1000 : Date.now() / 1000;
    const radiance = getPlaneRadiance(this.level, this.planeColor, now);
    ctx.save();
    const flame = 28 + Math.sin(performance.now() / 70) * 7;
    const gradient = ctx.createLinearGradient(this.x, this.y + 30 * scale, this.x, this.y + (30 + flame) * scale);
    gradient.addColorStop(0, '#ffffff');
    gradient.addColorStop(0.25, radiance.glowColor || this.trailColor);
    gradient.addColorStop(1, 'rgba(70,230,255,0)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(this.x - 13 * scale, this.y + 26 * scale);
    ctx.lineTo(this.x, this.y + (36 + flame) * scale);
    ctx.lineTo(this.x + 13 * scale, this.y + 26 * scale);
    ctx.closePath(); ctx.fill();
    ctx.shadowColor = radiance.glowColor;
    ctx.shadowBlur = radiance.glowBlur * scale;
    if (this.fighterImage && this.fighterImage.complete !== false) {
      ctx.drawImage(this.fighterImage, this.x - 58 * scale, this.y - 66 * scale, 116 * scale, 132 * scale);
      ctx.globalCompositeOperation = 'screen';
      ctx.globalAlpha = 0.06 + radiance.progress * 0.3 + radiance.pulse * radiance.progress * 0.08;
      ctx.drawImage(this.fighterImage, this.x - 58 * scale, this.y - 66 * scale, 116 * scale, 132 * scale);
      ctx.globalAlpha = 0.28 + radiance.progress * 0.52;
      ctx.strokeStyle = radiance.highlightColor;
      ctx.lineWidth = (1.5 + radiance.progress * 2.5) * scale;
      ctx.beginPath();
      ctx.ellipse(this.x, this.y, 48 * scale, 57 * scale, 0, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      drawPlane(ctx, this.x, this.y, scale * 1.12, this.planeColor, radiance);
      ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 4 * scale;
      ctx.beginPath();
      ctx.moveTo(this.x - 28 * scale, this.y - 18 * scale); ctx.lineTo(this.x - 28 * scale, this.y - 48 * scale);
      ctx.moveTo(this.x + 28 * scale, this.y - 18 * scale); ctx.lineTo(this.x + 28 * scale, this.y - 48 * scale);
      ctx.stroke();
    }
    ctx.restore();
    drawRadianceSparkles(ctx, this.x, this.y, scale * 1.08, radiance);
  }

  getHitCircle() {
    return { x: this.x, y: this.y, radius: this.radius };
  }
}

module.exports = Player;
